import { supabase } from "./config";

// Group forests are the teams in sritaru_teams; a contribution joins one
// through sritaru_contributions.team_id. Only public columns are read (the
// same ones the Wall shows: first name, trees, dedication, status, dates).

// Trees that are paid for or planted; pledges are counted separately.
export const COUNTED_STATUSES = ["planted", "paid", "zelle_paid", "gpay_paid"];
export const CURRENT_YEAR = 2026;

// Short links for the group forests, by team name (db/sritaru-forests.sql).
export const FOREST_SLUGS = {
  recw: "RECW / NITW",
  nitw: "RECW / NITW",
  brindavanam: "B+ Kathakala Brindavanam",
  manabadi: "ManaBadi",
};
const slugFor = (name) => Object.keys(FOREST_SLUGS).find((k) => FOREST_SLUGS[k] === name);

// Group forests (alumni, WhatsApp groups) vs the captains' "Team ..." teams.
export const isGroupForest = (name) => !/^Team\s/i.test(name || "");

// "ManaBadi forest", but "B+ Kathakala Brindavanam" (already a grove).
export const forestLabel = (name) => (/(forest|vanam)$/i.test(name) ? name : `${name} forest`);

// /forest/recw for a forest with a short link, else /forest/<id>.
export const forestPath = (f) => `/forest/${slugFor(f.name) || f.id}`;
export const forestUrl = (f) => `https://sritaru.kathakala.us${forestPath(f)}`;

// A ?team= or /forest/ value (id or short link) -> the team's id, given the teams list.
export function resolveTeam(value, teams) {
  if (!value) return "";
  const v = String(value).toLowerCase();
  const name = FOREST_SLUGS[v];
  const t = teams.find((t) => (name ? t.team_name === name : String(t.team_id) === v));
  return t ? String(t.team_id) : "";
}

// Next round number a forest is growing towards.
const MILESTONES = [10, 25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000];
export const nextMilestone = (n) => MILESTONES.find((m) => m > n) || Math.ceil((n + 1) / 50000) * 50000;

function summarize(team, rows) {
  const counted = rows.filter((r) => COUNTED_STATUSES.includes(r.payment_status));
  const sum = (rs) => rs.reduce((s, r) => s + (r.num_trees || 0), 0);
  return {
    id: team.team_id,
    name: team.team_name,
    trees: sum(counted),
    treesThisYear: sum(counted.filter((r) => r.year === CURRENT_YEAR)),
    pledged: sum(rows.filter((r) => r.payment_status === "pledged")),
    donors: rows.length,
    lastAt: rows.reduce((m, r) => (r.created_at && r.created_at > m ? r.created_at : m), ""),
  };
}

// All forests with their counts, biggest first.
export async function fetchForests() {
  const [{ data: teams, error: e1 }, { data: rows, error: e2 }] = await Promise.all([
    supabase.from("sritaru_teams").select("team_id, team_name"),
    supabase.from("sritaru_contributions").select("team_id, num_trees, payment_status, year, created_at").not("team_id", "is", null),
  ]);
  if (e1 || e2) throw e1 || e2;
  const byTeam = {};
  (rows || []).forEach((r) => (byTeam[r.team_id] = byTeam[r.team_id] || []).push(r));
  return (teams || [])
    .map((t) => summarize(t, byTeam[t.team_id] || []))
    .sort((a, b) => b.trees - a.trees || b.pledged - a.pledged || a.name.localeCompare(b.name));
}

// One forest (by id or short link), its rank among its kind and its recent dedications.
export async function fetchForest(idOrSlug) {
  const all = await fetchForests();
  const id = resolveTeam(idOrSlug, all.map((f) => ({ team_id: f.id, team_name: f.name })));
  const f = all.find((x) => String(x.id) === id);
  if (!f) return null;
  const { data: rows, error } = await supabase
    .from("sritaru_contributions")
    .select("first_name, num_trees, dedicated_to, payment_status, year, created_at")
    .eq("team_id", f.id)
    .order("created_at", { ascending: false, nullsFirst: false });
  if (error) throw error;
  const kind = all.filter((x) => isGroupForest(x.name) === isGroupForest(f.name));
  return { ...f, rank: kind.indexOf(f) + 1, of: kind.length, recent: (rows || []).slice(0, 20) };
}
